/**
 * The canvas's own controls — `docs/property-maps-v2.md` §5.3, board 2.
 *
 * Two clusters the map library would happily draw for us, and does not:
 *
 * **Zoom and locate, top-right.** MapLibre's `NavigationControl` would be
 * MapLibre's markup in MapLibre's colours inside a screen built from this
 * repo's tokens, so these are 32px buttons of ours driving the same camera
 * methods. **Locate goes back to the property**, not to where the phone is:
 * this is an office screen looking at somebody else's yard, and a browser
 * geolocation prompt in the middle of drawing a plan answers a question
 * nobody asked.
 *
 * **The scale bar and the imagery credit, bottom-right.** `attributionControl`
 * is off because §9.1's terms require the attribution to be visible and
 * unobscured — which is a layout decision, not a library default — so this is
 * where it lives, over the photo, in white on an ink wash so it reads on both
 * a parking lot and a lawn. The bar's distance is `scaleBar()` in
 * `shared/src/geo.ts`, where the print page and the crew app can reach it.
 */
import { useEffect, useState, type ReactNode } from 'react'
import type { Map as MapLibreMap } from 'maplibre-gl'
import { metresPerPixel, scaleBar } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { IconLocate, IconMinus, IconPlus } from '../../frame/icons.js'

export function ZoomStack({ map, onHome, touch }: {
  map: MapLibreMap | null
  /** Back to the property — the pin this map was opened around. */
  onHome: () => void
  /**
   * §7: 44px hit area on a finger, at any width from 768px up.
   *
   * The glyph stays 15px — what a thumb needs is somewhere to land, not a
   * bigger drawing — so the stack goes from 32 to 44 and the icon does not.
   */
  touch?: boolean
}) {
  return (
    <div className="absolute right-3 top-3 flex flex-col gap-2">
      <div className="flex flex-col overflow-hidden rounded-button bg-surface shadow-popover">
        <ChromeButton
          label="Zoom in"
          touch={touch}
          onClick={() => map?.zoomIn()}
          className="border-b border-line"
        >
          <IconPlus size={15} />
        </ChromeButton>
        <ChromeButton label="Zoom out" touch={touch} onClick={() => map?.zoomOut()}>
          <IconMinus size={15} />
        </ChromeButton>
      </div>
      <div className="overflow-hidden rounded-button bg-surface shadow-popover">
        <ChromeButton label="Back to the property" touch={touch} onClick={onHome}>
          <IconLocate size={15} />
        </ChromeButton>
      </div>
    </div>
  )
}

function ChromeButton({ label, onClick, className, touch, children }: {
  label: string
  onClick: () => void
  className?: string
  touch?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'flex items-center justify-center text-text-body hover:bg-hover',
        touch ? 'h-11 w-11' : 'h-8 w-8',
        className,
      )}
    >
      {children}
    </button>
  )
}

/**
 * `50 ft ▁▁▁` and `Imagery © Google · captured Apr 2026`.
 *
 * The bar is as wide as the distance it names, which is why the width comes
 * back from `scaleBar()` rather than being a constant: a bar drawn 90px and
 * labelled 50 ft when 50 ft is 71px is a ruler that lies.
 */
export function ScaleAndCredit({ map, attribution, capturedAt, onPlain }: {
  map: MapLibreMap | null
  attribution: string | null
  /** The imagery's own date, where the provider gives one. */
  capturedAt?: string | null
  /**
   * True when the base is the flat one (§5.7, and the crew app offline).
   *
   * White with a drop shadow is right over a photograph and invisible over a
   * near-white background — the bar is the one piece of chrome that has to
   * stay readable on both, because somebody measures off it.
   */
  onPlain?: boolean
}) {
  const [bar, setBar] = useState<{ label: string; pixels: number } | null>(null)

  useEffect(() => {
    if (!map) return undefined
    const measure = () => {
      const { lat } = map.getCenter()
      setBar(scaleBar(metresPerPixel(lat, map.getZoom())))
    }
    measure()
    map.on('move', measure)
    return () => { map.off('move', measure) }
  }, [map])

  const credit = attribution
    ? `Imagery © ${attribution}${capturedAt ? ` · captured ${capturedWords(capturedAt)}` : ''}`
    : null

  if (!bar && !credit) return null

  return (
    <div className="absolute bottom-3 right-3 flex items-end gap-2.5">
      {bar && (
        <div className="flex flex-col items-start gap-0.5">
          <span className={cn(
            'text-[10px] font-semibold',
            onPlain ? 'text-text-body' : 'text-white [text-shadow:0_1px_2px_#000]',
          )}
          >
            {bar.label}
          </span>
          {/*
            * Three sides of a box: the U-shape every map draws, because two
            * end ticks and a rule say "from here to here" in a way a plain
            * line does not.
            */}
          <span
            aria-hidden
            className={cn(
              'block h-1.5 border-2 border-t-0',
              onPlain ? 'border-text-body' : 'border-white',
            )}
            style={{ width: `${Math.round(bar.pixels)}px` }}
          />
        </div>
      )}
      {credit && (
        <span className={cn(
          'rounded-tag bg-text/35 px-1.5 py-0.5 text-[10px] text-white',
          '[text-shadow:0_1px_2px_#000]',
        )}
        >
          {credit}
        </span>
      )}
    </div>
  )
}

/** `Apr 2026` from whatever shape the provider's date arrives in. */
function capturedWords(value: string): string {
  const when = new Date(value)
  if (Number.isNaN(when.getTime())) return value
  return when.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}
